import { Logger } from 'utilities';

type FallbackStorageKey = string | ((storageKey: string) => string);

type FallbackDeps = {
  memoryFallback: Map<string, unknown>;
  fallbackStorageKey: FallbackStorageKey;
  logNamespace: string;
};

export const idbRequest = <T,>(req: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export const getFallbackStorageKey = (key: string, fallbackStorageKey: FallbackStorageKey): string =>
  typeof fallbackStorageKey === 'function' ? fallbackStorageKey(key) : fallbackStorageKey;

export const attachVersionChangeHandler = (
  db: IDBDatabase,
  onConnectionClose: () => void,
): void => {
  // Close gracefully when another tab requests a version upgrade so that
  // tab's open request is not blocked indefinitely.
  const connection = db;
  connection.onversionchange = () => {
    connection.close();
    onConnectionClose();
  };
};

export const handleUpgradeNeeded = (event: IDBVersionChangeEvent, storeName: string): void => {
  const db = (event.target as IDBOpenDBRequest).result;
  if (!db.objectStoreNames.contains(storeName)) {
    db.createObjectStore(storeName, { keyPath: 'key' });
  }
};

export const handleOpenSuccess = (
  event: Event,
  resolve: (db: IDBDatabase) => void,
  clearOpenTimeout: () => void,
  onConnectionClose: () => void,
): void => {
  clearOpenTimeout();
  const db = (event.target as IDBOpenDBRequest).result;
  attachVersionChangeHandler(db, onConnectionClose);
  resolve(db);
};

export const handleOpenError = (
  error: DOMException | null,
  reject: (reason?: unknown) => void,
  clearOpenTimeout: () => void,
  onConnectionClose: () => void,
): void => {
  clearOpenTimeout();
  onConnectionClose();
  reject(error);
};

export const handleOpenBlocked = (logNamespace: string): void => {
  // A prior connection is holding an older DB version; the timeout will reject
  // if onsuccess does not fire once that connection closes.
  Logger.warn(`${logNamespace} IndexedDB open blocked by another connection`);
};

export const getFallback = <T,>({
  key,
  memoryFallback,
  fallbackStorageKey,
  logNamespace,
}: FallbackDeps & { key: string }): T | undefined => {
  if (memoryFallback.has(key)) {
    return memoryFallback.get(key) as T;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const raw = window.localStorage.getItem(getFallbackStorageKey(key, fallbackStorageKey));
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch (error) {
    Logger.debug(`${logNamespace} fallback read failed:`, error);
    return undefined;
  }
};

export const setFallback = ({
  key,
  value,
  memoryFallback,
  fallbackStorageKey,
  logNamespace,
}: FallbackDeps & { key: string; value: unknown }): void => {
  memoryFallback.set(key, value);

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getFallbackStorageKey(key, fallbackStorageKey), JSON.stringify(value));
  } catch (error) {
    Logger.debug(`${logNamespace} fallback write failed; using memory fallback:`, error);
  }
};

export const removeFallback = ({
  key,
  memoryFallback,
  fallbackStorageKey,
  logNamespace,
}: FallbackDeps & { key: string }): void => {
  memoryFallback.delete(key);

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(getFallbackStorageKey(key, fallbackStorageKey));
  } catch (error) {
    Logger.debug(`${logNamespace} fallback removal failed:`, error);
  }
};
